"use client";

// Ported from relay-protocol packages/relay-widget/src/components/OrdersList.tsx
// (v0.1.0), then REDESIGNED in place on 2026-07-30 ("Transfer history — card
// components, per-chain split" mockup). The data plumbing is untouched — same
// props, same GET /v1/orders?user= shape via useRelayOrders, same
// formatting/explorer helpers — but the PRESENTATION changed twice over:
//
//   * the 6-column grid table is gone. A column grid could not survive the
//     narrow surfaces this list actually lives on (a 26rem desktop modal, a
//     mobile drawer): amounts wrapped mid-digit and the container query that
//     hid columns just traded one illegibility for another. Each receipt is
//     now a CARD — asset+status on top, sent → received amounts, date+chevron
//     below — a vertical stack that has no columns to squeeze.
//   * ONE CARD = ONE DEPOSIT, when the backend has attributed them. The
//     sweeper aggregates every deposit sitting at the reusable address into
//     ONE order paid out by ONE delivery, so a Base deposit and a BNB deposit
//     used to melt into a single "Multiple · 2 chains" row. The user signed
//     two transactions; they now see two cards, each naming its own chain and
//     linking its own origin tx. The delivered amount is allocated across the
//     split cards pro rata (see allocateProRata) and marked "~" — the split is
//     presentation, the payout on-chain was one transfer.
//
// The card list intentionally has no header row and no list title of its own
// unless asked (`showTitle`). In modal mode (`showTitle=false`) the list is also
// frameless: the shell is already the surface and every transfer has its own
// card, so another card around the whole stack is redundant.
import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon } from "../../wallet-info/components/icons";
import { formatUnits } from "../lib/units";
import { chainDisplayName, explorerTxLink } from "../lib/explorer";
import type { Catalog, OrderDeposit, OrderSummary } from "../lib/types";
import "../styles/component.css";

export interface OrdersListProps {
  orders: OrderSummary[];
  isLoading: boolean;
  error?: Error;
  /** Used to format amounts in their own token's decimals (origin looked up by
   * chainId + currency, delivery by the order's target symbol). */
  catalog?: Catalog;
  /** Render the list's own framed "Transfer history" section (default true).
   * The history modal passes false: its shell already owns the accessible title
   * and surface, so the list becomes a bare stack of transfer cards. */
  showTitle?: boolean;
}

export function HistoryEmptyState({ requiresIdentity = false }: { requiresIdentity?: boolean }) {
  return (
    <div
      className="rd-history-empty"
      role="status"
      data-variant={requiresIdentity ? "disconnected" : "empty"}
      data-testid={requiresIdentity ? "rd-history-empty-state" : "rd-history-no-transfers"}
    >
      <span className="rd-history-empty-icon" aria-hidden="true">
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" focusable="false">
          <path
            d="M7 3.8h7l3 3V12M7 3.8A2.2 2.2 0 0 0 4.8 6v12A2.2 2.2 0 0 0 7 20.2h5"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M14 3.8V7h3" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
          <circle cx="16.5" cy="16.5" r="3.7" stroke="currentColor" strokeWidth="1.7" />
          <path
            d="M16.5 14.5v2.2l1.4.8"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <h3 className="rd-history-empty-title">
        {requiresIdentity ? "Connect to view history" : "No transfers yet"}
      </h3>
      <p className="rd-history-empty-copy">
        {requiresIdentity
          ? "Connect a wallet or provide an address to see transfer history."
          : "Your deposits and their delivery progress will appear here."}
      </p>
      {!requiresIdentity && (
        <span className="rd-history-empty-live">
          <span aria-hidden="true" />
          Updates automatically
        </span>
      )}
    </div>
  );
}

/** BSC chain id: the bridge leg always runs here, and an unattributed deposit's
 * arrival tx is a BSC tx. */
const BSC_CHAIN_ID = 56;

function isTerminalFailure(status: string): boolean {
  return status === "FAILED" || status === "REFUNDED" || status === "RELAY_REFUNDED" || status === "EXPIRED";
}

/** API lifecycle values are useful diagnostics, but not suitable primary UI
 * copy. Known states get task-oriented labels; an unfamiliar future state is
 * still made readable without hiding it entirely. */
function orderStatusLabel(status: string): string {
  const normalized = status.trim().toUpperCase();
  const known: Record<string, string> = {
    AWAITING_DEPOSIT: "Waiting for deposit",
    BSC_FUNDED: "Deposit confirmed",
    RELAY_PENDING: "Bridging",
    RELAY_SUBMITTED: "Bridging",
    BRIDGING: "Bridging",
    CROSS_PENDING: "Delivering",
    FINALIZING: "Delivering",
    EXECUTING: "Delivering",
    COMPLETED: "Completed",
    RELAY_REFUNDED: "Refunded",
    REFUNDED: "Refunded",
    EXPIRED: "Expired",
    FAILED: "Failed",
  };
  const label = known[normalized];
  if (label) return label;
  const words = normalized.toLowerCase().replace(/_/g, " ").trim();
  return words ? `${words[0].toUpperCase()}${words.slice(1)}` : "Processing";
}

function statusPillClassName(status: string): string {
  if (status === "COMPLETED") return "rd-status-pill rd-status-pill-success";
  if (isTerminalFailure(status)) return "rd-status-pill rd-status-pill-failure";
  return "rd-status-pill";
}

/** Renders a smallest-unit amount string in human units, falling back to the
 * raw string when decimals aren't known (unmatched origin) or the value is
 * empty/malformed -- amounts are best-effort display, never load-bearing. */
function formatAmount(raw: string | undefined, decimals: number | undefined): string | undefined {
  if (!raw) return undefined;
  if (decimals === undefined) return raw;
  try {
    return formatUnits(BigInt(raw), decimals);
  } catch {
    return raw;
  }
}

/** displayAmount shortens a full-precision amount for a card, keeping the
 * exact value for the portalled hover tooltip.
 *
 * An 18-decimal token renders as e.g. "0.968446245970557194", which buries the
 * magnitude the figure exists to convey. Four decimal places are enough for
 * scanning, and truncating (not rounding) means the
 * shown figure never claims the user has more than they do. The untruncated
 * value stays one hover away. */
function displayAmount(full: string, maxDecimals = 4): string {
  const dot = full.indexOf(".");
  if (dot < 0) return full;
  const trimmed = full.slice(0, dot + 1 + maxDecimals).replace(/0+$/, "").replace(/\.$/, "");
  return trimmed === "" ? "0" : trimmed;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function shortHash(hash: string): string {
  return hash.length > 14 ? `${hash.slice(0, 8)}...${hash.slice(-4)}` : hash;
}

function HistoryAmount({
  amount,
  symbol,
  className,
}: {
  amount?: string;
  symbol?: string;
  className: string;
}) {
  const full = amount ? `${amount}${symbol ? ` ${symbol}` : ""}` : "";
  const tooltipId = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({
    top: -9999,
    left: -9999,
    visibility: "hidden",
  });

  useEffect(() => {
    if (!tooltipOpen) return;

    const positionTooltip = () => {
      const anchor = anchorRef.current;
      const tooltip = tooltipRef.current;
      if (!anchor || !tooltip) return;

      const anchorRect = anchor.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportPadding = 12;
      const gap = 8;
      const maxLeft = Math.max(viewportPadding, window.innerWidth - tooltipRect.width - viewportPadding);
      const left = Math.min(
        Math.max(anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2, viewportPadding),
        maxLeft,
      );
      const above = anchorRect.top - tooltipRect.height - gap;
      const preferredTop = above >= viewportPadding ? above : anchorRect.bottom + gap;
      const maxTop = Math.max(viewportPadding, window.innerHeight - tooltipRect.height - viewportPadding);
      const top = Math.min(Math.max(preferredTop, viewportPadding), maxTop);
      const computed = window.getComputedStyle(anchor);

      setTooltipStyle({
        top,
        left,
        visibility: "visible",
        backgroundColor: computed.getPropertyValue("--rd-text").trim() || "#eef0f6",
        borderColor: computed.getPropertyValue("--rd-border").trim() || "#2a2d3a",
        boxShadow: computed.getPropertyValue("--rd-shadow").trim() || "0 8px 24px rgba(0, 0, 0, 0.35)",
        color: computed.getPropertyValue("--rd-surface").trim() || "#171923",
      });
    };

    positionTooltip();
    window.addEventListener("resize", positionTooltip);
    window.addEventListener("scroll", positionTooltip, true);
    return () => {
      window.removeEventListener("resize", positionTooltip);
      window.removeEventListener("scroll", positionTooltip, true);
    };
  }, [tooltipOpen]);

  if (!amount) return <span className={className}>-</span>;

  return (
    <>
      <span
        ref={anchorRef}
        className={className}
        aria-label={full}
        aria-describedby={tooltipOpen ? tooltipId : undefined}
        data-full-value={full}
        onMouseEnter={() => {
          setTooltipStyle({ top: -9999, left: -9999, visibility: "hidden" });
          setTooltipOpen(true);
        }}
        onMouseLeave={() => setTooltipOpen(false)}
      >
        {displayAmount(amount)}{symbol ? ` ${symbol}` : ""}
      </span>
      {tooltipOpen && typeof document !== "undefined" && createPortal(
        <span
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          data-rd-history-floating-tooltip=""
          style={tooltipStyle}
        >
          {full}
        </span>,
        document.body,
      )}
    </>
  );
}

/** What an UN-SPLIT card's asset line describes (single-deposit orders, plus
 * multi-deposit orders whose amounts failed to parse — see allocateProRata).
 *
 * Read from the DEPOSITS, not the order: the order records BSC USDT for every
 * standing deposit regardless of where the funds came from (one forwarder serves
 * every origin route). The order's own origin is the correct fallback for a
 * direct BSC deposit, and for any deposit the backend has not attributed yet.
 *
 * `routes` only matters on the fallback path now: a splittable order renders one
 * card per deposit and never consults it, but when the split is impossible the
 * single card must still say "Multiple" rather than naming whichever deposit
 * happened to be first. */
function originOf(order: OrderSummary): { chainId: number; currency: string; routes: number } {
  const attributed = (order.deposits ?? []).filter((d) => d.originChainId);
  const distinct = new Set(attributed.map((d) => `${d.originChainId}:${(d.originCurrency ?? "").toLowerCase()}`));
  if (attributed.length > 0) {
    const first = attributed[0];
    return { chainId: first.originChainId!, currency: first.originCurrency ?? "", routes: distinct.size };
  }
  return { chainId: order.originChainId, currency: order.originCurrency, routes: 1 };
}

/**
 * Splits the order's delivered amount across its deposits pro rata, entirely in
 * BigInt (CLAUDE.md §4 — wei-scale values never touch Number): each share is
 * `total * deposit / sum` floored by BigInt division, and the dust that
 * flooring shaves off is added to the LAST share so the shares always sum to
 * the exact total. Floor-then-top-up is deliberate: no card ever claims more
 * than its true share, and nothing the user received disappears.
 *
 * Returns undefined when any input fails to parse or the deposit sum is zero —
 * callers fall back to the un-split single card rather than guessing.
 */
function allocateProRata(totalRaw: string | undefined, deposits: OrderDeposit[]): bigint[] | undefined {
  if (!totalRaw) return undefined;
  let total: bigint;
  let amounts: bigint[];
  try {
    total = BigInt(totalRaw);
    amounts = deposits.map((d) => BigInt(d.amount));
  } catch {
    return undefined;
  }
  const sum = amounts.reduce((a, b) => a + b, 0n);
  if (sum <= 0n) return undefined;
  const shares = amounts.map((a) => (total * a) / sum);
  const allocated = shares.reduce((a, b) => a + b, 0n);
  shares[shares.length - 1] += total - allocated;
  return shares;
}

/** One deposit/bridge/deliver leg inside an expanded card. `shared` marks a tx
 * that several split cards point at (the sweep/delivery happened ONCE for the
 * whole order) — labeling it beats letting the same hash on two cards read as
 * a duplication bug. */
function StepRow({
  label,
  chainId,
  txHash,
  amount,
  shared,
  missingLabel = "Pending",
}: {
  label: string;
  chainId: number;
  txHash?: string;
  amount?: string;
  shared?: boolean;
  /** A completed/failed order with an absent hash means the detail was not
   * recorded, not that the already-terminal step is still pending. */
  missingLabel?: string;
}) {
  const link = txHash ? explorerTxLink(chainId, txHash) : undefined;
  return (
    <div className="rd-history-step">
      <span className={`rd-history-step-dot${txHash ? " rd-history-step-dot-done" : ""}`} />
      <span className="rd-history-step-label">{label}</span>
      <span className="rd-history-step-chain">{chainDisplayName(chainId)}</span>
      {amount && <HistoryAmount amount={amount} className="rd-history-step-amount" />}
      <span className="rd-history-step-tx">
        {link ? (
          <a
            href={link.url}
            target="_blank"
            rel="noreferrer"
            title={txHash}
            aria-label={`${label} transaction on ${chainDisplayName(chainId)}`}
          >
            {shortHash(txHash!)}
          </a>
        ) : txHash ? (
          // A hash we have but no explorer for is still worth showing -- the
          // user can paste it somewhere. Silently dropping it would look like
          // the step never happened.
          <span title={txHash}>{shortHash(txHash)}</span>
        ) : (
          <span className="rd-muted">{missingLabel}</span>
        )}
        {shared && txHash && (
          <span className="rd-history-step-shared" title="Shared by deposits processed together">
            combined transfer
          </span>
        )}
      </span>
    </div>
  );
}

/** Everything one card needs, precomputed so the split and un-split paths feed
 * the identical renderer. */
interface HistoryCard {
  key: string;
  order: OrderSummary;
  /** Chain/currency the asset line names (a deposit's own attribution, or the
   * originOf fallback). */
  chainId: number;
  currency: string;
  /** > 1 only on the un-split fallback path — renders the "Multiple · N chains"
   * label instead of one arbitrary chain. */
  routes: number;
  /** Smallest-unit BSC USDT string: the deposit's own arrival on a split card,
   * the order's whole amountIn otherwise. */
  sentRaw: string | undefined;
  /** Smallest-unit delivered amount (allocated share on a split card). */
  receivedRaw: string | undefined;
  /** True renders "~" + muted: the pre-delivery projection on an un-split
   * card, and EVERY split card — a pro-rata share is an approximation even
   * when the order's total was observed. */
  receivedIsEstimate: boolean;
  /** The one deposit this card's expanded view details. Unset = un-split card,
   * which lists every attributed deposit (or the not-recorded note). */
  deposit?: OrderDeposit;
  /** Split cards share the order's bridge/deliver txs — label them so. */
  sharedLegs: boolean;
}

/** An order becomes one card per attributed deposit when there are several and
 * the amounts parse; otherwise exactly one card, byte-identical in content to
 * the pre-split behavior. */
function cardsOf(order: OrderSummary): HistoryCard[] {
  const deposits = order.deposits ?? [];
  const outRaw = order.amountOut || order.expectedOut || undefined;

  if (deposits.length >= 2) {
    const shares = allocateProRata(outRaw, deposits);
    if (shares) {
      return deposits.map((d, i) => ({
        key: `${order.orderId}:${d.txHash}:${d.blockNumber}`,
        order,
        chainId: d.originChainId ?? BSC_CHAIN_ID,
        currency: d.originCurrency ?? "",
        routes: 1,
        sentRaw: d.amount,
        receivedRaw: shares[i].toString(),
        receivedIsEstimate: true,
        deposit: d,
        sharedLegs: true,
      }));
    }
  }

  const { chainId, currency, routes } = originOf(order);
  return [
    {
      key: order.orderId,
      order,
      chainId,
      currency,
      routes,
      sentRaw: order.amountIn,
      receivedRaw: outRaw,
      receivedIsEstimate: !order.amountOut && Boolean(order.expectedOut),
      sharedLegs: false,
    },
  ];
}

export function OrdersList({ orders, isLoading, error, catalog, showTitle = true }: OrdersListProps) {
  // Which cards are expanded, by card key. Collapsed by default: the card face
  // answers the common question, and the legs are for when something looks wrong.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <section className={showTitle ? "rd-card" : "rd-stack"} data-rd-orders-list="">
      {showTitle && (
        <div className="rd-card-header">
          <h2 className="rd-card-title">Transfer history</h2>
        </div>
      )}

      {isLoading && orders.length === 0 && (
        <p className="rd-muted" role="status" aria-live="polite">
          <span className="rd-spinner" /> Loading orders...
        </p>
      )}

      {error && (
        <div className="rd-callout rd-callout-danger">
          Couldn&apos;t load your orders: {error.message}
        </div>
      )}

      {!isLoading && !error && orders.length === 0 && (
        <HistoryEmptyState />
      )}

      {orders.length > 0 && (
        <div className="rd-history-table" data-vaul-no-drag>
          {orders.flatMap(cardsOf).map((card) => {
            const { order } = card;
            const origin = catalog?.origins.find(
              (o) => o.chainId === card.chainId && o.token.address.toLowerCase() === card.currency.toLowerCase(),
            );

            // The "out" side is the order's actual delivery target (e.g.
            // pONEUSD), not the single default destination -- resolve it from
            // catalog.targets by the order's target symbol, falling back to the
            // default target, then to catalog.destination.
            const deliveryTarget =
              (order.target
                ? catalog?.targets.find((t) => t.symbol.toLowerCase() === order.target!.toLowerCase())
                : undefined) ?? catalog?.targets.find((t) => t.isDefault);
            const outSymbol = deliveryTarget?.symbol ?? catalog?.destination.symbol ?? "";
            const outDecimals = deliveryTarget?.decimals ?? catalog?.destination.decimals;

            // BSC USDT decimals for the swept "in" amount. Both amountIn and a
            // deposit's own `amount` are what reached the forwarder, so they are
            // denominated in the bridged token, NOT in the origin token the user
            // sent.
            const bscUsdt = catalog?.origins.find((o) => o.chainId === BSC_CHAIN_ID);
            const sent = formatAmount(card.sentRaw, bscUsdt?.token.decimals);
            const sentSymbol = bscUsdt?.token.symbol ?? "USDT";
            const received = formatAmount(card.receivedRaw, outDecimals);

            const deposits: OrderDeposit[] = order.deposits ?? [];
            const isOpen = Boolean(expanded[card.key]);
            const when = formatTimestamp(order.completedAt ?? order.createdAt);
            const statusLabel = orderStatusLabel(order.status);
            const terminal = order.status === "COMPLETED" || isTerminalFailure(order.status);
            const missingTxLabel = terminal ? "Details unavailable" : "Pending";
            const receivedLabel = card.receivedIsEstimate
              ? "Estimated on ONEchain"
              : "Received on ONEchain";

            return (
              <div key={card.key} className="rd-history-group">
                <button
                  type="button"
                  className="rd-history-row-button"
                  onClick={() => toggle(card.key)}
                  aria-expanded={isOpen}
                  aria-label={`Toggle transfer details: ${statusLabel}, ${when}`}
                >
                  <span className="rd-history-card-top">
                    <span className="rd-history-asset">
                      {card.routes > 1 ? (
                        // Several chains funded this one receipt and the split
                        // was impossible -- say so instead of naming whichever
                        // deposit happened to be first.
                        <>
                          Multiple deposits <span className="rd-muted">· {card.routes} chains</span>
                        </>
                      ) : (
                        <>
                          {origin?.token.symbol ?? outSymbol}{" "}
                          <span className="rd-muted">from {chainDisplayName(card.chainId)}</span>
                        </>
                      )}
                    </span>
                    <span className={statusPillClassName(order.status)} title={order.status}>
                      {statusLabel}
                    </span>
                  </span>

                  <span className="rd-history-card-amounts">
                    <span className="rd-history-amount-block">
                      <span className="rd-history-amount-label">Processing address</span>
                      <HistoryAmount amount={sent} symbol={sentSymbol} className="rd-history-num" />
                    </span>
                    <span className="rd-history-card-arrow" aria-hidden="true">
                      →
                    </span>
                    <span className="rd-history-amount-block">
                      <span className="rd-history-amount-label">{receivedLabel}</span>
                      <HistoryAmount
                        amount={received}
                        symbol={outSymbol}
                        className={`rd-history-num rd-history-received${
                          card.receivedIsEstimate ? " rd-history-received-estimate" : ""
                        }`}
                      />
                    </span>
                  </span>

                  <span className="rd-history-card-foot">
                    <span className="rd-history-date">
                      {order.completedAt ? "Completed" : "Started"} · {when}
                    </span>
                    <span className="rd-history-chevron" aria-hidden="true">
                      <ChevronDownIcon size={16} />
                    </span>
                  </span>
                </button>

                {isOpen && (
                  <div className="rd-history-detail">
                    {card.deposit ? (
                      // A split card details ITS OWN deposit leg only -- the
                      // sibling cards carry the others.
                      <StepRow
                        label="Deposit confirmed"
                        // An attributed deposit links the user's ORIGIN-chain
                        // send; an unattributed one has only its BSC arrival.
                        chainId={card.deposit.originChainId ?? BSC_CHAIN_ID}
                        txHash={card.deposit.originTxHash || card.deposit.txHash}
                        amount={(() => {
                          const a = formatAmount(card.deposit.amount, bscUsdt?.token.decimals);
                          return a;
                        })()}
                      />
                    ) : deposits.length > 0 ? (
                      deposits.map((d) => (
                        <StepRow
                          key={`${d.txHash}-${d.blockNumber}`}
                          label="Deposit confirmed"
                          chainId={d.originChainId ?? BSC_CHAIN_ID}
                          txHash={d.originTxHash || d.txHash}
                          amount={(() => {
                            const a = formatAmount(d.amount, bscUsdt?.token.decimals);
                            return a;
                          })()}
                        />
                      ))
                    ) : (
                      // No attributed deposits is not "no deposits": the funds
                      // demonstrably arrived (an order exists). Say which it is
                      // rather than rendering an empty step.
                      <div className="rd-history-step">
                        <span className="rd-history-step-dot" />
                        <span className="rd-history-step-label">Deposit confirmed</span>
                        <span className="rd-muted">Details unavailable</span>
                      </div>
                    )}
                    <StepRow
                      label="Bridge"
                      chainId={BSC_CHAIN_ID}
                      txHash={order.bscBridgeTx}
                      shared={card.sharedLegs}
                      missingLabel={missingTxLabel}
                    />
                    <StepRow
                      label="ONEchain delivery"
                      chainId={catalog?.destination.chainId ?? 612055}
                      txHash={order.deliveryTxHash}
                      shared={card.sharedLegs}
                      missingLabel={missingTxLabel}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
